import React, { useState, useEffect } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import Loader from '../../Components/Loader/Loader';
import { serviceConsumer } from '../../network/ServiceConsumer';
import { agesCode22 } from '../CreateFeisEvent/Components/SyllabusComp/SyllabusArrays';
import { rowFromComp, compFromRow } from './syllabusRows';

// Oireachtas Syllabus builder — built fresh to the create-oireachtas mockup
// (plain React, no Formik). Collapsible LEVELS (competition type implied by the
// level), the feis age-square multi-select (agesselected/agesunselected), and
// the extra Oireachtas columns (Drop H&L, Qualifier). Persists via
// PUT /oireachtas/:id/competitions; template load/save via the template routes.

const GENDERS = [['Girls','girls'],['Boys','boys'],['Both','boys_girls'],['Mixed','mixed'],['N/A','na']];

const blankComp = () => ({ code: '', description: '', price: '', ages: [], gender: 'girls', adjudicators: 5, rounds: 3, dropHighLow: false, qualifier: false });
const comp = (code, description, price, ages, gender, adjudicators, rounds, dropHighLow, qualifier) =>
  ({ code, description, price, ages, gender, adjudicators, rounds, dropHighLow, qualifier });

const SEED = [
  { title: 'Solo Championships', expanded: true, bulk: '', competitions: [
    comp('SC-10-G', 'Girls Under 10', 100, ['U10'], 'girls', 5, 3, true, true),
    comp('SC-99-G', 'Ladies 22 & Over', 100, ['22 AND OVER'], 'girls', 5, 3, true, true),
  ]},
  { title: 'Solo Championship Treble Reels', expanded: false, bulk: '', competitions: [
    comp('TR-11', 'Boys & Girls Under 11', 20, ['U11'], 'boys_girls', 1, 1, false, false),
  ]},
  { title: 'Teams', expanded: false, bulk: '', competitions: [
    comp('T4-12-M', '4-Hand Under 12', 20, ['U12'], 'mixed', 5, 1, true, true),
    comp('T6-10', '6-Hand Under 10', 20, ['U10'], 'na', 3, 1, false, false),
    comp('T8-15-G', 'Céilí 8-Hand Under 15', 20, ['U15'], 'girls', 5, 1, true, true),
  ]},
  { title: 'Premiere', expanded: false, bulk: '', competitions: [
    comp('PR-08', 'Premiere Under 8', 85, ['U8'], 'boys_girls', 3, 1, false, false),
  ]},
  { title: 'Premiere Treble Reels', expanded: false, bulk: '', competitions: [] },
];

const SyllabusFies = (props) => {
  const [loading, setLoading] = useState(false);
  const EventID = localStorage.getItem('ID');
  const [levels, setLevels] = useState(SEED);
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);

  const levelsFromFlat = (comps) => {
    const by = {};
    comps.forEach((c) => {
      const g = c.group || 'Ungrouped';
      (by[g] = by[g] || []).push(rowFromComp(c, agesCode22));
    });
    return Object.entries(by).map(([title, competitions], i) => ({ title, expanded: i === 0, bulk: '', competitions }));
  };

  useEffect(() => {
    (async () => {
      try {
        const t = await serviceConsumer('GET', `${process.env.REACT_APP_BASE_URL}/oireachtas/templates`);
        setTemplates(t.templates || []);
      } catch (e) { /* ignore */ }
      if (!EventID) return;
      try {
        setLoading(true);
        const res = await serviceConsumer('GET', `${process.env.REACT_APP_BASE_URL}/oireachtas/${EventID}/competitions`);
        if (res.competitions?.length) setLevels(levelsFromFlat(res.competitions));
      } catch (e) { /* new */ } finally { setLoading(false); }
    })();
  }, [EventID]);

  const upLevel = (gi, patch) => setLevels((gs) => gs.map((g, i) => i === gi ? { ...g, ...patch } : g));
  const upComp = (gi, ci, patch) => setLevels((gs) => gs.map((g, i) => i !== gi ? g : { ...g, competitions: g.competitions.map((c, j) => j === ci ? { ...c, ...patch } : c) }));
  const toggleAge = (gi, ci, n) => setLevels((gs) => gs.map((g, i) => i !== gi ? g : { ...g, competitions: g.competitions.map((c, j) => {
    if (j !== ci) return c;
    const ages = c.ages.includes(n) ? c.ages.filter((a) => a !== n) : [...c.ages, n];
    return { ...c, ages };
  }) }));
  const addComp = (gi) => setLevels((gs) => gs.map((g, i) => i === gi ? { ...g, competitions: [...g.competitions, blankComp()] } : g));
  const delComp = (gi, ci) => setLevels((gs) => gs.map((g, i) => i !== gi ? g : { ...g, competitions: g.competitions.filter((_, j) => j !== ci) }));
  const delLevel = (gi) => setLevels((gs) => gs.filter((_, i) => i !== gi));
  const addLevel = () => setLevels((gs) => [...gs, { title: 'New level', expanded: true, bulk: '', competitions: [] }]);
  const applyBulk = (gi) => setLevels((gs) => gs.map((g, i) => i === gi ? { ...g, competitions: g.competitions.map((c) => ({ ...c, price: g.bulk })) } : g));

  // drop high & low only with 5 judges (ECRO rule 9): a stored flag on a 1- or
  // 3-judge competition is cleared on save, never re-sent (Fable review 6075036d R1-F1)
  const flatten = () =>
    levels.flatMap((g) =>
      g.competitions
        .filter((c) => c.code)
        .map((c) => {
          const comp = compFromRow(c, g.title);
          return Number(comp.adjudicators) === 5 ? comp : { ...comp, dropHighLow: false };
        })
    );

  const saveCompetitions = (page) => serviceConsumer('PUT', `${process.env.REACT_APP_BASE_URL}/oireachtas/${EventID}/competitions`, { competitions: flatten(), page });

  const next = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const res = await saveCompetitions(4);
      if (res.success) {
        localStorage.setItem('TAB', 5);
        if (Number(localStorage.getItem('page')) < 4) localStorage.setItem('page', 4);
        setTimeout(() => props.setValue('5'), 200);
      }
    } catch (err) { toast.error(err?.message || 'Failed to save syllabus.'); }
    finally { setLoading(false); }
  };

  const loadTemplate = async (templateId) => {
    setShowTemplates(false);
    if (!templateId) return;
    try {
      setLoading(true);
      await serviceConsumer('POST', `${process.env.REACT_APP_BASE_URL}/oireachtas/${EventID}/clone-template`, { templateId });
      const res = await serviceConsumer('GET', `${process.env.REACT_APP_BASE_URL}/oireachtas/${EventID}/competitions`);
      if (res.competitions?.length) setLevels(levelsFromFlat(res.competitions));
      toast.success('Template loaded');
    } catch (err) { toast.error(err?.message || 'Failed to load template.'); }
    finally { setLoading(false); }
  };

  const saveTemplate = async () => {
    const name = window.prompt('Template name:');
    if (!name) return;
    try {
      setLoading(true);
      await saveCompetitions();
      await serviceConsumer('POST', `${process.env.REACT_APP_BASE_URL}/oireachtas/${EventID}/save-template`, { name });
      const t = await serviceConsumer('GET', `${process.env.REACT_APP_BASE_URL}/oireachtas/templates`);
      setTemplates(t.templates || []);
      toast.success('Saved as template');
    } catch (err) { toast.error(err?.message || 'Failed to save template.'); }
    finally { setLoading(false); }
  };

  const green = { color: '#588f36', fontWeight: 600, cursor: 'pointer' };
  const greenBtn = { border: '1.5px solid #68ad3f', color: '#588f36', background: '#fff', borderRadius: 10, padding: '10px 18px', cursor: 'pointer', fontWeight: 600 };
  const input = { border: '1px solid #dcdcdc', borderRadius: 10, padding: '10px 12px' };
  const colHead = { fontSize: 12, fontWeight: 700, color: '#656565', letterSpacing: '0.03em', padding: '0 8px 8px' };

  return (
    <div>
      {loading && <Loader />}
      {!loading && (
        <div style={{ maxWidth: 1400 }}>
          <div style={{ border: '1px solid #eaeaea', borderRadius: 12, padding: '18px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 20, marginBottom: 24 }}>
            <div style={{ fontSize: 20, fontWeight: 800 }}>Syllabus templates</div>
            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ position: 'relative' }}>
                <button type="button" onClick={() => setShowTemplates((s) => !s)} style={{ ...greenBtn, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <FileDownloadOutlinedIcon fontSize="small" /> Load template
                </button>
                {showTemplates && (
                  <div style={{ position: 'absolute', top: '110%', right: 0, background: '#fff', border: '1px solid #dcdcdc', borderRadius: 10, boxShadow: '0 6px 20px rgba(0,0,0,0.12)', minWidth: 220, zIndex: 30, overflow: 'hidden' }}>
                    {templates.length === 0 && <div style={{ padding: '10px 14px', color: '#999' }}>No saved templates yet</div>}
                    {templates.map((t) => (
                      <div key={t._id} onClick={() => loadTemplate(t._id)} style={{ padding: '10px 14px', cursor: 'pointer' }}>{t.name}</div>
                    ))}
                  </div>
                )}
              </div>
              <button type="button" onClick={saveTemplate} style={{ background: '#68ad3f', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 18px', cursor: 'pointer', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <SaveOutlinedIcon fontSize="small" /> Save as template
              </button>
            </div>
          </div>

          {levels.map((g, gi) => (
            <div key={gi} style={{ borderBottom: '1px solid #eaeaea', padding: '24px 0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                <input value={g.title} onChange={(e) => upLevel(gi, { title: e.target.value })}
                  style={{ border: 'none', background: 'transparent', fontSize: 22, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.01em', minWidth: 320, outline: 'none' }} />
                <span onClick={() => delLevel(gi)} style={{ ...green, display: 'inline-flex', alignItems: 'center', gap: 4 }}><DeleteOutlineIcon fontSize="small" /> Delete level</span>
                <button type="button" onClick={() => upLevel(gi, { expanded: !g.expanded })} style={{ marginLeft: 'auto', border: 'none', background: 'none', cursor: 'pointer', fontSize: 20, color: '#656565' }}>{g.expanded ? '⌃' : '⌄'}</button>
              </div>

              {g.expanded && (
                <div style={{ marginTop: 16 }}>
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ fontSize: 15, marginBottom: 8 }}>Change general price:</div>
                    <div style={{ display: 'flex', gap: 14 }}>
                      <input placeholder="Price" type="number" value={g.bulk} onChange={(e) => upLevel(gi, { bulk: e.target.value })} style={{ ...input, width: 280 }} />
                      <button type="button" onClick={() => applyBulk(gi)} style={greenBtn}>Update Level</button>
                    </div>
                  </div>

                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead><tr>
                        {['CODE','DESCRIPTION','PRICE','AGES','GENDER','#ADJUDICATORS','ROUNDS','DROP H&L','QUALIFIER',''].map((h, i) => <th key={i} style={{ ...colHead, textAlign: 'left' }}>{h}</th>)}
                      </tr></thead>
                      <tbody>
                        {g.competitions.map((c, ci) => (
                          <tr key={ci} style={{ borderTop: '1px solid #f0f0f0', verticalAlign: 'middle' }}>
                            <td style={{ padding: '10px 8px' }}><input value={c.code} onChange={(e) => upComp(gi, ci, { code: e.target.value })} style={{ ...input, width: 80, textAlign: 'center' }} /></td>
                            <td style={{ padding: '10px 8px' }}><input value={c.description} onChange={(e) => upComp(gi, ci, { description: e.target.value })} style={{ ...input, width: 180 }} /></td>
                            <td style={{ padding: '10px 8px' }}><input type="number" value={c.price} onChange={(e) => upComp(gi, ci, { price: e.target.value })} style={{ ...input, width: 80, textAlign: 'center' }} /></td>
                            <td style={{ padding: '10px 8px' }}>
                              <div className="agesCodetd">
                                {agesCode22.map((n) => (
                                  <label key={n} className={c.ages.includes(n) ? 'agesselected' : 'agesunselected'}>
                                    <input type="checkbox" checked={c.ages.includes(n)} onChange={() => toggleAge(gi, ci, n)} />{n}
                                  </label>
                                ))}
                              </div>
                            </td>
                            <td style={{ padding: '10px 8px' }}><select value={c.gender} onChange={(e) => upComp(gi, ci, { gender: e.target.value })} style={{ ...input, width: 95 }}>{GENDERS.map(([l, v]) => <option key={v} value={v}>{l}</option>)}</select></td>
                            <td style={{ padding: '10px 8px' }}><select value={c.adjudicators} onChange={(e) => upComp(gi, ci, { adjudicators: e.target.value, ...(Number(e.target.value) === 5 ? {} : { dropHighLow: false }) })} style={{ ...input, width: 75 }}>{[1,3,5].map((n) => <option key={n} value={n}>{n}</option>)}</select></td>
                            <td style={{ padding: '10px 8px' }}><select value={c.rounds} onChange={(e) => upComp(gi, ci, { rounds: e.target.value })} style={{ ...input, width: 70 }}>{[1,2,3].map((n) => <option key={n} value={n}>{n}</option>)}</select></td>
                            <td style={{ padding: '10px 8px', textAlign: 'center' }}><input type="checkbox" checked={!!c.dropHighLow} disabled={Number(c.adjudicators) !== 5 && !c.dropHighLow} title={Number(c.adjudicators) === 5 ? '' : 'Drop high & low needs 5 judges'} onChange={(e) => upComp(gi, ci, { dropHighLow: e.target.checked })} style={{ width: 20, height: 20, accentColor: '#68ad3f' }} /></td>
                            <td style={{ padding: '10px 8px', textAlign: 'center' }}><input type="checkbox" checked={c.qualifier} onChange={(e) => upComp(gi, ci, { qualifier: e.target.checked })} style={{ width: 20, height: 20, accentColor: '#68ad3f' }} /></td>
                            <td style={{ padding: '10px 8px', textAlign: 'center' }}><DeleteOutlineIcon onClick={() => delComp(gi, ci)} style={{ color: '#588f36', cursor: 'pointer' }} /></td>
                          </tr>
                        ))}
                        {g.competitions.length === 0 && <tr><td colSpan={10} style={{ padding: 12, color: '#999' }}>No competitions yet.</td></tr>}
                      </tbody>
                    </table>
                  </div>

                  <button type="button" onClick={() => addComp(gi)} style={{ ...greenBtn, marginTop: 16 }}>+ Add competition</button>
                </div>
              )}
            </div>
          ))}

          <button type="button" onClick={addLevel} style={{ ...green, background: 'none', border: 'none', fontSize: 16, marginTop: 20 }}>+ Add level</button>

          <div className="mt-4 mb-5" style={{ textAlign: 'right' }}>
            <button className="filled" type="button" onClick={next}>Next</button>
          </div>
        </div>
      )}
      <ToastContainer />
    </div>
  );
};

export default SyllabusFies;
